(function () {
  'use strict';

  // ============================================================
  // BRAND CONFIG — the values most likely to change per brand.
  // Jockey NZ: free-shipping threshold is $49 NZD (checked live
  // against jockey.co.nz/pages/shipping-options, 2026-09-18) —
  // must match the amount shown in poc-pdp-trust-box.js.
  // ============================================================
  const BRAND = {
    fontBody: '"Nunito Sans", sans-serif',
    fontHeading: '"Montserrat", sans-serif',
    fontWeightHeading: '400',
    colorText: '#001135',
    colorBorder: '#e2e4e8',
    colorBg: '#f5f5f5',
    accent: '#001135', // progress-bar fill color; distinct from colorText for brands with a separate accent
    radius: '0px', // container/track/fill corner radius
    textTextTransform: 'none',
    textLetterSpacing: '0',
    thresholdCents: 4900, // must match the amount shown in the trust box / shipping page
  };

  const BAR_CSS = `
    #poc-free-shipping-bar{
      font-family: ${BRAND.fontBody};
      background: ${BRAND.colorBg};
      border: 1px solid ${BRAND.colorBorder};
      border-top: none;
      text-align: center;
      /* Starts at zero height/opacity instead of a hard hidden->shown snap:
         the bar can only appear once the /cart.js fetch resolves (a network
         round-trip after the rest of the page is already visible), so an
         instant pop-in reads as a layout jolt. Animating max-height/opacity/
         padding together makes the space open up smoothly instead. */
      overflow: hidden;
      max-height: 0;
      padding: 0 16px;
      opacity: 0;
      transition: max-height 0.3s ease, opacity 0.25s ease, padding 0.3s ease;
    }
    #poc-free-shipping-bar.poc-fsb-visible{
      max-height: 60px;
      padding: 8px 16px;
      opacity: 1;
    }
    #poc-free-shipping-bar .poc-fsb-text{
      font-size: 12px;
      font-family: ${BRAND.fontHeading};
      font-weight: ${BRAND.fontWeightHeading};
      color: ${BRAND.colorText};
      margin: 0 0 5px;
      text-transform: ${BRAND.textTextTransform};
      letter-spacing: ${BRAND.textLetterSpacing};
    }
    #poc-free-shipping-bar .poc-fsb-text .poc-fsb-amount{
      color: ${BRAND.accent};
    }
    #poc-free-shipping-bar .poc-fsb-track{
      max-width: 420px;
      height: 6px;
      margin: 0 auto;
      border-radius: ${BRAND.radius};
      background: ${BRAND.colorBorder};
      overflow: hidden;
    }
    #poc-free-shipping-bar .poc-fsb-fill{
      height: 100%;
      border-radius: ${BRAND.radius};
      background: ${BRAND.accent};
      transition: width 0.35s ease;
      width: 0%;
    }
    /* Skeleton/loading state — see showSkeleton() below for why. */
    #poc-free-shipping-bar .poc-fsb-text,
    #poc-free-shipping-bar .poc-fsb-track{
      transition: filter 0.25s ease, opacity 0.25s ease;
    }
    #poc-free-shipping-bar.poc-fsb-loading .poc-fsb-text,
    #poc-free-shipping-bar.poc-fsb-loading .poc-fsb-track{
      filter: blur(3px);
      opacity: 0.55;
    }
  `;

  function formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  function injectStyle() {
    if (document.getElementById('poc-free-shipping-bar-style')) return;
    const style = document.createElement('style');
    style.id = 'poc-free-shipping-bar-style';
    style.textContent = BAR_CSS;
    document.head.appendChild(style);
  }

  function ensureBar() {
    let bar = document.getElementById('poc-free-shipping-bar');
    if (bar) return bar;

    // Anchored to whichever trust box is on the page (PDP's #poc-trust-box
    // from poc-pdp-trust-box.js, or a cart-page equivalent with id
    // "poc-cart-trust-box"), rendering directly beneath it. A standalone
    // banner right below the site header was tried and rejected: this
    // theme's header is position:absolute inside a 0-height wrapper, so an
    // in-flow sibling there renders on top of the header instead of below
    // it. The trust box is a reliable, already-proven in-flow anchor for
    // shipping-related trust content instead.
    const anchor = document.getElementById('poc-trust-box') || document.getElementById('poc-cart-trust-box');
    if (!anchor) return null;

    bar = document.createElement('div');
    bar.id = 'poc-free-shipping-bar';

    const text = document.createElement('p');
    text.className = 'poc-fsb-text';

    const track = document.createElement('div');
    track.className = 'poc-fsb-track';
    const fill = document.createElement('div');
    fill.className = 'poc-fsb-fill';
    track.appendChild(fill);

    bar.appendChild(text);
    bar.appendChild(track);

    anchor.insertAdjacentElement('afterend', bar);
    return bar;
  }

  // Shows the container immediately, in a blurred placeholder state, before
  // the real cart data is known — see BAR_CSS's .poc-fsb-loading rules.
  function showSkeleton() {
    injectStyle();
    const bar = ensureBar();
    if (!bar) return;
    const text = bar.querySelector('.poc-fsb-text');
    const fill = bar.querySelector('.poc-fsb-fill');
    text.textContent = 'Checking free shipping progress…';
    // Starts empty (0%), not a plausible-looking guess: an arbitrary
    // placeholder fill risks visibly shrinking back if the shopper's actual
    // progress is less than the guess, which reads as the bar "going
    // backwards" — worse than starting from zero.
    fill.style.width = '0%';
    bar.classList.add('poc-fsb-visible', 'poc-fsb-loading');
  }

  function render(cart) {
    const bar = ensureBar();
    if (!bar) return;

    bar.classList.remove('poc-fsb-loading');

    if (!cart || cart.item_count < 1) {
      bar.classList.remove('poc-fsb-visible');
      return;
    }

    const text = bar.querySelector('.poc-fsb-text');
    const fill = bar.querySelector('.poc-fsb-fill');
    const remaining = BRAND.thresholdCents - cart.total_price;
    const pct = Math.min(100, Math.max(0, (cart.total_price / BRAND.thresholdCents) * 100));

    if (remaining <= 0) {
      text.textContent = "You've unlocked free shipping!";
    } else {
      text.innerHTML = '';
      text.append(
        'You\'re ',
        Object.assign(document.createElement('span'), { className: 'poc-fsb-amount', textContent: formatMoney(remaining) }),
        ' away from free shipping'
      );
    }
    fill.style.width = `${pct}%`;
    bar.classList.add('poc-fsb-visible');
  }

  function refresh() {
    injectStyle();
    fetch('/cart.js', { credentials: 'same-origin' })
      .then((res) => res.json())
      .then(render)
      .catch(() => {});
  }

  // Coalesces a MutationObserver callback to at most once per animation
  // frame — a raw callback re-runs on every single DOM mutation, which is
  // wasteful when watching document.body's whole subtree from document-start.
  function debounceRaf(fn) {
    let scheduled = false;
    return (...args) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        fn(...args);
      });
    };
  }

  // Watches indefinitely rather than a fixed-timeout retry, since the trust
  // box anchor may come from a separately-timed script on some pages.
  function waitForAnchorThenRefresh() {
    const tryRefresh = () => {
      const anchor = document.getElementById('poc-trust-box') || document.getElementById('poc-cart-trust-box');
      if (!anchor) return false;
      // Show the (blurred) container the instant the anchor exists, instead
      // of waiting for the /cart.js round trip that refresh() kicks off.
      // Only done on the initial load, not from the click/cart-request
      // handlers below: those refreshes update a bar that's typically
      // already showing real content, so re-blurring on every Add to Cart
      // click would be flicker, not a helpful loading state.
      showSkeleton();
      refresh();
      return true;
    };

    if (tryRefresh()) return;

    const observer = new MutationObserver(
      debounceRaf(() => {
        if (tryRefresh()) observer.disconnect();
      })
    );
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Catches every real cart mutation — PDP Add to Cart, and the /cart page's
  // own quantity +/-, typed quantity, and Remove controls — by watching for
  // Shopify's standard AJAX cart endpoints directly rather than guessing
  // from button text or a `cart:updated` event (this theme doesn't dispatch
  // one). Patches both XHR (what this theme's cart form actually uses) and
  // fetch (in case any flow uses it instead).
  function watchCartRequests(onCartChanged) {
    const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

    const origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const result = origFetch.apply(this, arguments);
        if (CART_MUTATION_RE.test(url)) {
          result.then(() => onCartChanged()).catch(() => {});
        }
        return result;
      };
    }

    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (CART_MUTATION_RE.test(String(url))) {
        this.addEventListener('loadend', onCartChanged);
      }
      return origOpen.apply(this, arguments);
    };
  }

  function init() {
    waitForAnchorThenRefresh();

    // Small delay so Shopify's own cart state has settled server-side
    // before we re-read /cart.js.
    watchCartRequests(() => setTimeout(refresh, 150));

    // Belt-and-braces: covers a theme/app that does dispatch this event,
    // even though this theme doesn't.
    document.addEventListener('cart:updated', refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
